import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { useCampaignTimeFrame } from "modules/campaigns/components/Detail/CampaignTimeFrameContext"
import { useCampaignViewFormat } from "modules/campaigns/components/Detail/CampaignViewFormatContext"
import { GroupedTreemap } from "modules/dashboard/components/Treemaps/GroupedTreemap"
import { GroupedTreemapTable } from "modules/dashboard/components/Treemaps/GroupedTreemapTable"
import { useTranslation } from "react-i18next"
import { CanonicalTopicGroupedByEnum, ClusterDomainEnum, GoalResponseDataSchema } from "schema"
import z from "zod"

type Props = {
  campaignId?: string
  goal: z.infer<typeof GoalResponseDataSchema>
}

export const OpenGoalInsights = ({ campaignId, goal }: Props) => {
  const { t } = useTranslation()
  const { timeFrame } = useCampaignTimeFrame()
  const { viewFormat } = useCampaignViewFormat()

  const title = goal.displayName ?? t("insights.openGoal.title")
  const description = goal.assistantInstructions

  const header = description ? (
    <Tooltip>
      <TooltipTrigger slick>
        <span className="text-md font-medium">{title}</span>
      </TooltipTrigger>
      <TooltipContent>{description}</TooltipContent>
    </Tooltip>
  ) : (
    <span className="text-md font-medium">{title}</span>
  )

  const treemapProps = {
    domain: ClusterDomainEnum.VoiceOfCustomer,
    groupedBy: { type: CanonicalTopicGroupedByEnum.company },
    timeFrame,
    goalId: goal.id,
    goalType: goal.type,
    goalLabel: goal.displayName,
    tagBySentiment: true,
    filterBy: { campaignId },
    title: t("insights.openGoal.treemap.title"),
    description: t("insights.openGoal.treemap.description"),
  } as const

  return (
    <div className="flex flex-col gap-3 w-full">
      {header}
      {viewFormat === "table" ? (
        <GroupedTreemapTable {...treemapProps} />
      ) : (
        <GroupedTreemap {...treemapProps} />
      )}
    </div>
  )
}
